import React, { useState, useEffect } from 'react';
import { MusicTrack } from '../constants';
import { getAudioDurationFromUrl } from '@/libs/getAudioDuration';
import Icon from '@/components/atoms/Icon';

interface AudioTrackCardProps {
  track: MusicTrack;
  isSelected: boolean;
  onClick: (trackId: string) => void;
  isPlaying?: boolean;
  onPlayPause?: (trackId: string) => void;
  disabled?: boolean;
}

/**
 * Formats duration in seconds to MM:SS format
 */
const formatDuration = (seconds: number): string => {
  if (!isFinite(seconds) || seconds < 0) {
    return '0:00';
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/**
 * AudioTrackCard - Displays a music track card with play button, name, artist, duration, and description.
 * Visual appearance changes based on selected state.
 * Supports audio preview playback via play/pause button.
 */
const AudioTrackCard: React.FC<AudioTrackCardProps> = ({
  track,
  isSelected,
  onClick,
  isPlaying = false,
  onPlayPause,
  disabled = false
}) => {
  const [duration, setDuration] = useState<number | null>(track.duration ?? null);

  useEffect(() => {
    if (track.duration !== undefined) {
      setDuration(track.duration);
      return;
    }

    getAudioDurationFromUrl(track.url)
      .then(setDuration)
      .catch(() => setDuration(null));
  }, [track]);

  const formattedDuration = duration !== null ? formatDuration(duration) : null;

  const handleClick = () => {
    if (disabled) {
      return;
    }
    onClick(track.id);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) {
      return;
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick();
    }
  };

  const handlePlayPauseClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) {
      return;
    }
    if (onPlayPause) {
      onPlayPause(track.id);
    }
  };

  const hasArtist = track.artist && track.artist.trim() !== '';
  const hasDescription = track.description && track.description.trim() !== '';
  const hasDuration = formattedDuration !== null;
  const hasMetadata = hasDuration || hasArtist;

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      data-testid="audio-track-card"
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={`group w-full rounded-lg border-2 p-4 text-left outline-none ring-0 transition-all ${
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'cursor-pointer hover:border-card-border hover:bg-card-hover-bg focus:outline-none focus:ring-0'
      } ${
        isSelected
          ? 'border-card-active-border bg-card-hover-bg hover:border-card-active-border'
          : 'border-card-border bg-white'
      }`}
      aria-label={hasArtist ? `Select track: ${track.name} by ${track.artist}` : `Select track: ${track.name}`}
      aria-disabled={disabled}
    >
      <div className="flex items-start gap-3">
        {/* Play/Pause Button Icon */}
        <button
          type="button"
          data-testid="audio-track-play-button"
          onClick={handlePlayPauseClick}
          disabled={disabled}
          className={`relative z-10 flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-full transition-all duration-100 hover:scale-105 ${
            disabled
              ? 'cursor-not-allowed opacity-50'
              : isPlaying || isSelected
                ? 'play-audio-button-active'
                : 'play-audio-button'
          } ${isSelected ? 'text-white' : 'text-gray-700 hover:text-white group-hover:text-white'}`}
          aria-label={isPlaying ? 'Pause audio' : 'Play audio'}
        >
          {isPlaying ? (
            <Icon name="IconPlayerPauseFilled" size={20} className="text-white" />
          ) : (
            <Icon name="IconPlayerPlayFilled" size={20} className="ml-0.5 transition-colors" />
          )}
        </button>

        {/* Track Info */}
        <div className="min-w-0 flex-1">
          {/* Track Name */}
          <div data-testid="audio-track-name" className="truncate text-sm font-medium leading-5 text-slate-800">
            {track.name || 'Untitled Track'}
          </div>

          {/* Metadata: Duration and/or Artist */}
          {hasMetadata && (
            <div
              data-testid="audio-track-metadata"
              className="mt-0.5 flex items-center gap-1 truncate text-xs text-gray-400"
            >
              {hasDuration && (
                <>
                  <span>{formattedDuration}</span>
                  {hasArtist && <span>-</span>}
                </>
              )}
              {hasArtist && <span data-testid="audio-track-artist">{track.artist}</span>}
            </div>
          )}

          {/* Description */}
          {hasDescription && (
            <div data-testid="audio-track-description" className="mt-1 line-clamp-2 text-xs text-gray-400">
              {track.description}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AudioTrackCard;
